import { withAlpha, type Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { MouseEventArgs } from "../core/events.js";
import type { Size } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";

export interface Objective {
  text: string;
  /** How many of the thing have been done. */
  count?: number;
  /** How many are needed. Omitted makes it a simple done/not-done. */
  required?: number;
  done?: boolean;
}

export interface Quest {
  id: string;
  title: string;
  objectives: readonly Objective[];
  /** Drawn folded up. */
  collapsed?: boolean;
  /** Picked out from the rest — the one being followed. */
  tracked?: boolean;
  /** Overrides the title color: a level warning, a faction, a timer running out. */
  color?: Color | null;
}

export interface QuestTrackerOptions extends ControlOptions {
  quests?: readonly Quest[];
  titleFont?: FontSpec | null;
  font?: FontSpec | null;
  /** Indent of the objectives under their title. */
  indent?: number;
  /** Gray out and tick objectives that are done, rather than dropping them. */
  showCompleted?: boolean;
  /** Shrink-wrap the control to its contents on every change. */
  autoSize?: boolean;
  onQuestClick?: (quest: Quest, index: number) => void;
  /** An objective's line was clicked. See `QuestTracker.onObjectiveClick`. */
  onObjectiveClick?: ObjectiveClickHandler | null;
}

/**
 * Told which objective line was clicked: the quest it is under and where that quest is in
 * the list, and the objective's index in `quest.objectives` — the quest's own array, so it
 * means the same with `showCompleted` off, when done objectives are not drawn.
 */
export type ObjectiveClickHandler = (quest: Quest, objectiveIndex: number, questIndex: number) => void;

const ROW_GAP = 2;
const QUEST_GAP = 8;
const TICK_SIZE = 10;

/**
 * The list of quests and objectives pinned to the side of the screen.
 *
 * Drawn rather than built out of controls, and deliberately: a tracker is
 * rewritten wholesale every time an objective ticks over, and rebuilding a
 * subtree of labels for "3/10 wolves slain" becoming "4/10" is a great deal
 * of machinery for a string. Everything here is one paint pass over a plain
 * array, and the only interactive parts — clicking a title to fold it, and
 * clicking an objective to hear which one — are one hit test against the rows
 * it just drew.
 */
export class QuestTracker extends Control {
  #quests: Quest[];
  /** Where each title ended up last paint, for hit testing. */
  #titleRows: { index: number; y: number; height: number }[] = [];
  /** Where each objective ended up last paint: its quest's index, and its own in that quest's objectives. */
  #objectiveRows: { index: number; objective: number; y: number; height: number }[] = [];

  #showCompleted: boolean;
  #autoSize: boolean;

  titleFont: FontSpec | null;
  font: FontSpec | null;
  indent: number;

  /**
   * Called when an objective's line is clicked, which folds nothing. Settable after
   * construction, so whatever owns the quest log can wire it up once it exists — a click
   * on "Wolves slain 4/10" is usually how a player asks to see that quest.
   */
  onObjectiveClick: ObjectiveClickHandler | null;

  constructor(options: QuestTrackerOptions = {}) {
    super({ width: 240, height: 160, tabStop: false, ...options });
    this.#quests = [...(options.quests ?? [])];
    this.titleFont = options.titleFont ?? null;
    this.font = options.font ?? null;
    this.indent = options.indent ?? 12;
    this.#showCompleted = options.showCompleted ?? true;
    this.#autoSize = options.autoSize ?? false;
    this.onObjectiveClick = options.onObjectiveClick ?? null;
    if (options.onQuestClick) {
      this.on("itemactivate", (index) => {
        const quest = this.#quests[index];
        if (quest) options.onQuestClick!(quest, index);
      });
    }
    this.on("click", (e) => this.#onClick(e));
  }

  get quests(): readonly Quest[] {
    return this.#quests;
  }
  set quests(v: readonly Quest[]) {
    this.#quests = [...v];
    this.#changed();
  }

  /** Gray out and tick objectives that are done, rather than dropping them. */
  get showCompleted(): boolean {
    return this.#showCompleted;
  }
  set showCompleted(v: boolean) {
    if (this.#showCompleted === v) return;
    this.#showCompleted = v;
    this.#changed();
  }

  /** Shrink-wrap the control to its contents on every change. */
  get autoSize(): boolean {
    return this.#autoSize;
  }
  set autoSize(v: boolean) {
    if (this.#autoSize === v) return;
    this.#autoSize = v;
    if (v) this.#applyAutoSize();
  }

  /**
   * The contents changed: repaint, re-measure, and tell a parent measuring
   * this control that the answer moved.
   */
  #changed(): void {
    // Measured before the event, so a listener that reads the height off the
    // back of a change reads the one the new contents need.
    this.#applyAutoSize();
    this.emit("change");
    this.invalidateMeasure();
    this.invalidate();
  }

  /**
   * Shrink-wrap to the contents, if that was asked for.
   *
   * Measured here rather than at the end of `onPaint`, which is where this
   * used to live and could not work: a tracker whose contents measured zero —
   * an empty quest log, which is how one usually starts — wrote itself a
   * height of 0, and a zero-height control is never painted again, so the one
   * piece of code that could have grown it back never ran. Driving it from the
   * data instead means the first quest added brings the tracker back, and a
   * `HudAnchor` reads a height that is already right rather than one a frame
   * behind.
   */
  #applyAutoSize(): void {
    if (!this.#autoSize) return;
    const desktop = this.desktop;
    if (!desktop) return;
    this.setSize(this.width, this.measureHeight(desktop.rc, desktop.theme));
  }

  protected override onEnvironmentChanged(): void {
    // Where a tracker built before it had a desktop gets its first real
    // measurement: until there is one there are no font metrics to measure with.
    this.#applyAutoSize();
    this.invalidateMeasure();
  }

  /**
   * As tall as the quests need, and as wide as it already is.
   *
   * The width is not measured: titles and objectives are drawn with a
   * `maxWidth` and clipped, so how much room a tracker takes across is the
   * application's choice rather than the text's.
   */
  override preferredSize(_available: Size): Size {
    const desktop = this.desktop;
    if (!desktop) return this.size;
    return { width: this.width, height: this.measureHeight(desktop.rc, desktop.theme) };
  }

  add(quest: Quest): void {
    this.#quests.push(quest);
    this.#changed();
  }

  remove(id: string): boolean {
    const i = this.#quests.findIndex((q) => q.id === id);
    if (i < 0) return false;
    this.#quests.splice(i, 1);
    this.#changed();
    return true;
  }

  find(id: string): Quest | null {
    return this.#quests.find((q) => q.id === id) ?? null;
  }

  /**
   * Rewrite one quest in place.
   *
   * Takes a whole quest rather than editing the one it holds: the caller's
   * quest log is the source of truth, and a tracker that mutated the object
   * it was handed would be writing back into it.
   */
  update(id: string, quest: Quest): boolean {
    const i = this.#quests.findIndex((q) => q.id === id);
    if (i < 0) return false;
    this.#quests[i] = quest;
    this.#changed();
    return true;
  }

  /** Fold or unfold one quest's objectives. */
  setCollapsed(id: string, collapsed: boolean): void {
    const quest = this.find(id);
    if (!quest || quest.collapsed === collapsed) return;
    this.update(id, { ...quest, collapsed });
  }

  /** True when every objective on the quest is done. */
  static isComplete(quest: Quest): boolean {
    return quest.objectives.every((o) => objectiveDone(o));
  }

  #onClick(e: MouseEventArgs): void {
    const within = (r: { y: number; height: number }) => e.y >= r.y && e.y < r.y + r.height;
    const row = this.#titleRows.find(within);
    if (row) {
      const quest = this.#quests[row.index];
      if (!quest) return;
      this.setCollapsed(quest.id, !quest.collapsed);
      this.emit("itemactivate", row.index);
      return;
    }

    const line = this.#objectiveRows.find(within);
    const quest = line && this.#quests[line.index];
    if (line && quest && quest.objectives[line.objective]) {
      this.onObjectiveClick?.(quest, line.objective, line.index);
    }
  }

  /** Height the contents need at the current width. */
  measureHeight(rc: RenderContext, theme: Theme): number {
    const titleStep = rc.lineHeight(this.titleFont ?? theme.metrics.captionFont);
    const step = rc.lineHeight(this.font ?? theme.metrics.font);
    let height = 0;
    for (const quest of this.#quests) {
      height += titleStep + ROW_GAP;
      if (!quest.collapsed) {
        height += this.#shown(quest).length * (step + ROW_GAP);
      }
      height += QUEST_GAP;
    }
    return Math.max(0, height - QUEST_GAP);
  }

  #shown(quest: Quest): readonly Objective[] {
    return this.#showCompleted
      ? quest.objectives
      : quest.objectives.filter((o) => !objectiveDone(o));
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    const titleFont = this.titleFont ?? theme.metrics.captionFont;
    const font = this.font ?? theme.metrics.font;
    const titleStep = rc.lineHeight(titleFont);
    const step = rc.lineHeight(font);

    this.#titleRows = [];
    this.#objectiveRows = [];
    let y = 0;

    this.#quests.forEach((quest, index) => {
      const complete = QuestTracker.isComplete(quest);
      const titleInk = quest.color ?? (complete ? p.textSubtle : quest.tracked ? p.accent : p.text);
      rc.drawText(quest.title, 0, y, titleFont, titleInk, {
        baseline: "top",
        maxWidth: this.width,
      });
      this.#titleRows.push({ index, y, height: titleStep });
      y += titleStep + ROW_GAP;

      if (!quest.collapsed) {
        for (const [objectiveIndex, objective] of quest.objectives.entries()) {
          const done = objectiveDone(objective);
          if (done && !this.#showCompleted) continue;
          // The gap under a line counts as the line, so there is no dead strip between two.
          this.#objectiveRows.push({ index, objective: objectiveIndex, y, height: step + ROW_GAP });
          const ink = done ? withAlpha(p.textSubtle, 0.7) : p.textSubtle;
          let x = this.indent;
          if (done) {
            theme.drawObjectiveMark(
              rc,
              { x, y: y + (step - TICK_SIZE) / 2, width: TICK_SIZE, height: TICK_SIZE },
              ink,
            );
            x += TICK_SIZE + 4;
          }
          rc.drawText(objectiveText(objective), x, y, font, ink, {
            baseline: "top",
            maxWidth: Math.max(0, this.width - x),
          });
          y += step + ROW_GAP;
        }
      }
      y += QUEST_GAP;
    });
  }
}

/** "Wolves slain 4/10", or just the text when there is nothing to count. */
export function objectiveText(objective: Objective): string {
  if (objective.required === undefined) return objective.text;
  return `${objective.text} ${objective.count ?? 0}/${objective.required}`;
}

/** True when an objective has been met, by its flag or by its count. */
export function objectiveDone(objective: Objective): boolean {
  if (objective.done !== undefined) return objective.done;
  if (objective.required === undefined) return false;
  return (objective.count ?? 0) >= objective.required;
}
